import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Switch,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/components/auth/AuthProvider';
import useAnalytics from '@/hooks/useAnalytics';
import { useBallotRecords } from '@/contexts/BallotRecordsContext';
import { type BallotDownloadSource } from '@/hooks/useGuestBallotDownload';
import { useBallotSheet } from '@/providers/BallotSheetProvider';
import { primitiveColors, semanticColors, BORDER_RADIUS } from '@/theme/tokens';
import { TOGGLE_SX } from '@/ui-library/switch.styles';
import { BallotRecordStatus, getBallotDotState, hasUpcomingElection } from '@/types/ballotRecords';
import { extraElectionCount, formatBallotRecordName } from '@/utils/ballotRecordName';
import BallotStatusDot from './BallotStatusDot';

const VISIBLE_RECORDS = 5;

/**
 * Status chip for the ballot header: shows the displayed ballot's
 * draft/saved state (with the status dot), and opens a popover with the
 * Save action, the auto-save toggle, and the ballot history picker.
 * For guests the chip reads Draft and Save routes to sign-in.
 */
interface BallotStatusButtonProps {
  /** Compact trigger — status dot + chevron only, no text label. Used in the
   * header / teal band where horizontal room is tight. */
  condensed?: boolean;
  /** Download the ballot PDF from the popup header (for a guest, prompt to sign
   * in); hidden when unavailable. */
  onDownload?: (source: BallotDownloadSource) => void;
  isDownloading?: boolean;
}

const BallotStatusButton: React.FC<BallotStatusButtonProps> = ({
  condensed = false,
  onDownload,
  isDownloading = false,
}) => {
  const { t, i18n } = useTranslation('ballot');
  const { isAuthenticated, signIn } = useAuth();
  const analytics = useAnalytics();
  const {
    records,
    currentRecord,
    viewedRecord,
    activeBallotId,
    activateBallot,
    saveRecord,
    setManualCompletion,
    autoSaveEnabled,
    setAutoSaveEnabled,
  } = useBallotRecords();
  const { setStatusPopoverOpen } = useBallotSheet();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);

  // The record whose status the chip reflects: an opened history record,
  // else the current-address record, else guest defaults.
  const displayed = useMemo(() => {
    if (viewedRecord) {
      return {
        id: viewedRecord.id,
        status: viewedRecord.status,
        upcoming: hasUpcomingElection(viewedRecord.elections),
        complete: Boolean(viewedRecord.is_complete),
        manuallyComplete: Boolean(viewedRecord.manually_complete),
      };
    }
    if (currentRecord) {
      return {
        id: currentRecord.id,
        status: currentRecord.status,
        upcoming: hasUpcomingElection(currentRecord.elections),
        complete: Boolean(currentRecord.is_complete),
        manuallyComplete: Boolean(currentRecord.manually_complete),
      };
    }
    return {
      id: null,
      status: 'draft' as BallotRecordStatus,
      upcoming: true,
      complete: false,
      manuallyComplete: false,
    };
  }, [viewedRecord, currentRecord]);

  const dotState = getBallotDotState(displayed.status, displayed.upcoming, displayed.complete);
  const isComplete = displayed.status === 'saved' && displayed.complete;
  const statusLabel = isComplete
    ? t('records.statusComplete')
    : displayed.status === 'saved'
      ? t('records.statusSaved')
      : t('records.statusDraft');
  const chipLabel = displayed.upcoming
    ? statusLabel
    : `${statusLabel} · ${t('records.statusPast')}`;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    // Keep the tap from reaching a wrapping tap-to-open/drag surface (the
    // mobile teal band is itself a button), so opening the popover doesn't
    // also toggle the ballot sheet.
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setStatusPopoverOpen(true);
    analytics.track('Ballot Status Popover Opened', {
      status: displayed.status,
      isAuthenticated,
    });
  };
  const handleClose = () => {
    setAnchorEl(null);
    setShowAll(false);
    setStatusPopoverOpen(false);
  };

  const handleDownload = () => {
    if (!onDownload) return;
    // A guest's download opens the sign-in dialog, which takes over from the popover.
    if (!isAuthenticated) handleClose();
    onDownload('status_popover');
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      analytics.track('Ballot Save Sign In Clicked');
      handleClose();
      signIn();
      return;
    }
    if (!displayed.id) return;
    setSaving(true);
    const ok = await saveRecord(displayed.id);
    setSaving(false);
    if (ok) analytics.track('Ballot Saved', { ballotId: displayed.id, source: 'status_control' });
  };

  const handleToggleAutoSave = async (_: unknown, checked: boolean) => {
    const ok = await setAutoSaveEnabled(checked);
    if (ok) analytics.track('Ballot Auto Save Toggled', { enabled: checked });
  };

  const handleToggleComplete = async (_: unknown, checked: boolean) => {
    if (!displayed.id) return;
    setCompleting(true);
    const ok = await setManualCompletion(displayed.id, checked);
    setCompleting(false);
    if (ok)
      analytics.track('Ballot Marked Complete Toggled', {
        enabled: checked,
        ballotId: displayed.id,
      });
  };

  const handlePickRecord = async (id: string) => {
    handleClose();
    analytics.track('Ballot Record Opened', { ballotId: id, source: 'status_popover' });
    // Selecting a ballot makes it the active selection so the left menu,
    // settings, ballot view, and chat context all follow it.
    await activateBallot(id);
  };

  // Only labeled entries (past + the address's soonest-upcoming) show as rows,
  // matching the left menu; later elections stay tab-only in the ballot view.
  const visibleEntries = records.filter((r) => r.is_visible_entry !== false);
  const visibleRecords = showAll ? visibleEntries : visibleEntries.slice(0, VISIBLE_RECORDS);
  const hint = !isAuthenticated
    ? t('records.guestHint')
    : !displayed.upcoming
      ? t('records.pastHint')
      : isComplete
        ? // The switch completes a ballot whatever its selections, so the
          // hint says which kind of complete this is rather than claiming
          // every contest has a pick.
          displayed.manuallyComplete
          ? t('records.manualCompleteHint')
          : t('records.completeHint')
        : displayed.status === 'saved'
          ? t('records.savedHint')
          : t('records.draftHint');

  const open = Boolean(anchorEl);

  return (
    <>
      {condensed ? (
        <ButtonBase
          onClick={handleOpen}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={t('records.statusButtonAriaLabel', { status: chipLabel })}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            pl: '6px',
            pr: '3px',
            py: '3px',
            borderRadius: BORDER_RADIUS,
            border: `1px solid ${semanticColors.textTertiary}`,
            backgroundColor: semanticColors.surface,
          }}
        >
          <BallotStatusDot state={dotState} />
          <ExpandMoreIcon sx={{ fontSize: 16, color: semanticColors.textTertiary }} />
        </ButtonBase>
      ) : (
        <Chip
          icon={
            <Box sx={{ display: 'flex', alignItems: 'center', pl: '6px' }}>
              <BallotStatusDot state={dotState} />
            </Box>
          }
          label={chipLabel}
          onClick={handleOpen}
          onDelete={handleOpen}
          deleteIcon={<ExpandMoreIcon />}
          size="small"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={t('records.statusButtonAriaLabel', { status: chipLabel })}
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 12,
            fontWeight: 600,
            borderRadius: BORDER_RADIUS,
            // textTertiary (#666) keeps the chip boundary ≥3:1 against the
            // white header (WCAG 1.4.11) — borderLight was ~1.3:1.
            border: `1px solid ${semanticColors.textTertiary}`,
            backgroundColor: semanticColors.surface,
            '& .MuiChip-deleteIcon': { color: semanticColors.textTertiary },
          }}
        />
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        // Sit above the mobile BallotSheet (``theme.zIndex.modal + 1``) and the
        // desktop right-side ballot strip (``BallotMenu``, 1600); otherwise the
        // popover renders behind them.
        sx={{ zIndex: (theme) => Math.max(theme.zIndex.modal + 2, 1601) }}
        slotProps={{
          paper: {
            // Force the ballot's exact DM Sans on every text node in the
            // popover. MUI Typography/Button/Chip each re-assert the theme font,
            // so a base fontFamily isn't enough — target them directly.
            sx: {
              width: 300,
              p: '14px',
              borderRadius: BORDER_RADIUS,
              fontFamily: "'DM Sans', sans-serif",
              '& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root': {
                fontFamily: "'DM Sans', sans-serif",
              },
            },
            role: 'dialog',
            'aria-modal': true,
            'aria-label': t('records.popoverAriaLabel'),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '6px' }}>
          <BallotStatusDot state={dotState} />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: semanticColors.textBody }}>
            {chipLabel}
          </Typography>
          {onDownload && (
            <IconButton
              size="small"
              aria-label={isDownloading ? t('menu.generatingPdf') : t('menu.downloadAriaLabel')}
              title={isAuthenticated ? undefined : t('menu.downloadSignInHint')}
              aria-busy={isDownloading}
              disabled={isDownloading}
              onClick={handleDownload}
              sx={{ ml: 'auto', color: semanticColors.textTertiary }}
            >
              {isDownloading ? (
                <CircularProgress size={16} />
              ) : (
                <ArrowDownTrayIcon width={18} height={18} />
              )}
            </IconButton>
          )}
        </Box>
        <Typography
          id="ballot-status-hint"
          sx={{ fontSize: 12.5, color: semanticColors.textTertiary, mb: '12px' }}
        >
          {hint}
        </Typography>

        {displayed.status === 'draft' && !autoSaveEnabled && (
          <Button
            fullWidth
            disableRipple
            aria-describedby="ballot-status-hint"
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              py: '7px',
              fontSize: 13,
              fontWeight: 600,
              backgroundColor: semanticColors.textPrimary,
              color: semanticColors.textOnPrimary,
              mb: '12px',
              '&:hover': { backgroundColor: semanticColors.brandAccent },
            }}
          >
            {saving ? (
              <CircularProgress size={16} sx={{ color: semanticColors.textOnPrimary }} />
            ) : isAuthenticated ? (
              t('records.saveBallot')
            ) : (
              t('records.signInToSave')
            )}
          </Button>
        )}

        {isAuthenticated && (
          <>
            {displayed.id && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: '12px',
                }}
              >
                <Box sx={{ pr: 1 }}>
                  <Typography
                    component="label"
                    htmlFor="ballot-complete-toggle"
                    sx={{ fontSize: 13, fontWeight: 600, color: semanticColors.textBody }}
                  >
                    {t('records.completeBallot')}
                  </Typography>
                  <Typography
                    id="ballot-complete-hint"
                    sx={{ fontSize: 11.5, color: semanticColors.textTertiary }}
                  >
                    {t('records.completeBallotHint')}
                  </Typography>
                </Box>
                <Switch
                  id="ballot-complete-toggle"
                  sx={TOGGLE_SX}
                  checked={displayed.manuallyComplete}
                  disabled={completing}
                  onChange={handleToggleComplete}
                  slotProps={{
                    input: { 'aria-describedby': 'ballot-complete-hint', 'aria-busy': completing },
                  }}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ pr: 1 }}>
                <Typography
                  component="label"
                  htmlFor="ballot-auto-save-toggle"
                  sx={{ fontSize: 13, fontWeight: 600, color: semanticColors.textBody }}
                >
                  {t('records.autoSave')}
                </Typography>
                <Typography
                  id="ballot-auto-save-hint"
                  sx={{ fontSize: 11.5, color: semanticColors.textTertiary }}
                >
                  {t('records.autoSaveHint')}
                </Typography>
              </Box>
              <Switch
                id="ballot-auto-save-toggle"
                sx={TOGGLE_SX}
                checked={Boolean(autoSaveEnabled)}
                disabled={autoSaveEnabled === null}
                onChange={handleToggleAutoSave}
                slotProps={{ input: { 'aria-describedby': 'ballot-auto-save-hint' } }}
              />
            </Box>

            {visibleEntries.length > 0 && (
              <>
                <Divider sx={{ my: '12px' }} />
                <Typography
                  component="h3"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: semanticColors.textTertiary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    mb: '4px',
                  }}
                >
                  {t('records.yourBallots')}
                </Typography>
                <Box role="list" id="ballot-status-records-list">
                  {visibleRecords.map((record) => {
                    const isActive =
                      record.is_active_entry === true ||
                      activeBallotId === record.id ||
                      (!activeBallotId && record.is_current_address);
                    const name = formatBallotRecordName(
                      record,
                      i18n.language,
                      t('records.unnamed')
                    );
                    const extra = extraElectionCount(record);
                    return (
                      <Box role="listitem" key={record.id}>
                        <ButtonBase
                          onClick={() => void handlePickRecord(record.id)}
                          aria-label={t('records.openAriaLabel', { name })}
                          sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 1,
                            px: '8px',
                            py: '6px',
                            borderRadius: BORDER_RADIUS,
                            textAlign: 'left',
                            backgroundColor: isActive ? semanticColors.borderLight : 'transparent',
                            '&:hover': {
                              backgroundColor: isActive
                                ? semanticColors.borderLight
                                : semanticColors.overlaySubtle,
                            },
                          }}
                        >
                          <BallotStatusDot
                            state={getBallotDotState(
                              record.status,
                              hasUpcomingElection(record.elections),
                              record.is_complete
                            )}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              noWrap
                              sx={{
                                fontSize: 13,
                                fontWeight: isActive ? 600 : 400,
                                color: semanticColors.textBody,
                              }}
                            >
                              {name}
                              {extra > 0 && ` ${t('records.moreElections', { count: extra })}`}
                            </Typography>
                            {record.location_label && (
                              <Typography
                                noWrap
                                sx={{ fontSize: 11, color: semanticColors.textTertiary }}
                              >
                                {record.location_label}
                              </Typography>
                            )}
                          </Box>
                        </ButtonBase>
                      </Box>
                    );
                  })}
                </Box>
                {visibleEntries.length > VISIBLE_RECORDS && (
                  <>
                    <Divider sx={{ my: '12px' }} />
                    <ButtonBase
                      disableRipple
                      onClick={() => {
                        setShowAll((prev) => !prev);
                        if (!showAll) analytics.track('Ballot History Expanded');
                      }}
                      aria-expanded={showAll}
                      aria-controls="ballot-status-records-list"
                      aria-label={showAll ? t('records.showLessAria') : t('records.showAllAria')}
                      sx={{
                        width: '100%',
                        display: 'block',
                        px: 2,
                        py: 0.5,
                        textAlign: 'center',
                        borderRadius: BORDER_RADIUS,
                        '&:hover': { backgroundColor: semanticColors.surfaceWarm },
                        '&:focus-visible': {
                          outline: `2px solid ${primitiveColors.logoPurple}`,
                          outlineOffset: '-2px',
                          zIndex: 1,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: 12, lg: 13 },
                          fontWeight: 600,
                          color: semanticColors.brandPrimary,
                        }}
                      >
                        {showAll ? t('records.showLess') : t('records.showAll')}
                      </Typography>
                    </ButtonBase>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Popover>
    </>
  );
};

export default BallotStatusButton;
